import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { AssistantSmartTipsEntity, AssistantSummaryReservation } from '@mews-commander/data-access';
import { useLocalizeText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { getFullName } from '@mews-commander/utils';
import { CustomerSummary } from '../customer/CustomerSummary';
import { useCustomerQuery } from '../../query/useCustomerQuery';
import { SearchStateProps } from '../../Assistant';
import { ReservationPrimaryQuickAction } from '../customer/ReservationQuickActions';
import { AssistantListItemSelectionStateProps } from '../../drawer/hooks/useAssistantListItemSelectionHandler';
import { SummarySearchTypeHookResult } from './useSummarySearchTypeHook';

interface SummaryCustomerHookProps {
    itemId: string;
    chainId: string;
    enterpriseIds: string[];
    handleCloseSummarySidePanel: () => void;
    setSearchState: Dispatch<SetStateAction<SearchStateProps>>;
    setListItemSelectionState: Dispatch<SetStateAction<AssistantListItemSelectionStateProps>>;
    summaryPanelOpened: boolean;
}

export interface ActiveReservationProps {
    activeReservations: AssistantSummaryReservation[];
    reservation: AssistantSummaryReservation | undefined;
    hasPastReservations: boolean;
    isInHouse: boolean;
    hasFutureReservations: boolean;
    hasMoreThanOneActiveReservation: boolean;
}

export const useSummaryCustomerHook = ({
    itemId,
    chainId,
    enterpriseIds,
    handleCloseSummarySidePanel,
    setSearchState,
    setListItemSelectionState,
    summaryPanelOpened,
}: SummaryCustomerHookProps): SummarySearchTypeHookResult => {
    const [activeReservationState, setActiveReservationState] = useState<ActiveReservationProps>({
        hasPastReservations: false,
        isInHouse: false,
        hasFutureReservations: false,
        hasMoreThanOneActiveReservation: false,
        activeReservations: [],
        reservation: undefined,
    });

    const { activeReservations, reservation, hasMoreThanOneActiveReservation } = activeReservationState;
    useEffect(() => {
        setActiveReservationState((prev) => ({
            ...prev,
            reservation: activeReservations[0] ?? undefined,
            hasMoreThanOneActiveReservation: activeReservations.length > 1,
        }));
    }, [activeReservations]);

    const { data: customer } = useCustomerQuery(itemId, summaryPanelOpened);
    const localizedText = useLocalizeText();

    return useMemo(() => {
        const customerSummary = customer ? (
            <CustomerSummary
                itemId={itemId}
                chainId={chainId}
                entity={AssistantSmartTipsEntity.Customer}
                enterpriseIds={enterpriseIds}
                setActiveReservationState={setActiveReservationState}
                customer={customer}
            />
        ) : null;

        const customerSummaryActions = customer ? (
            <ReservationPrimaryQuickAction
                data-test-smart-detail-footer
                hasMoreThanOneActiveReservation={hasMoreThanOneActiveReservation}
                reservation={reservation}
                customerId={itemId}
                hasPastReservations={activeReservationState.hasPastReservations}
                handleCloseSummarySidePanel={handleCloseSummarySidePanel}
                setSearchState={setSearchState}
                setListItemSelectionState={setListItemSelectionState}
            />
        ) : null;

        const title = customer ? getFullName(customer) : localizedText({ textKey: TextKey.Loading });

        return {
            component: customerSummary,
            title,
            actions: customerSummaryActions,
        };
    }, [
        activeReservationState.hasPastReservations,
        chainId,
        customer,
        enterpriseIds,
        handleCloseSummarySidePanel,
        hasMoreThanOneActiveReservation,
        itemId,
        localizedText,
        reservation,
        setListItemSelectionState,
        setSearchState,
    ]);
};
